import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin, ArrowRight, Calendar, Clock, Truck, Package,
  Phone, Mail, CheckCircle, XCircle, MessageSquare, ChevronRight,
  Home, Building2, User, FileText, Layers, X, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "../../components/DashboardLayout";
import CounterOfferModal from "./components/CounterOfferModal";
import { FleetrifyModal } from "../../components/Modal/modal";
import InfoTooltip from "../../components/Tooltip/InfoTooltip";
import {
  useGetRelocationOrdersQuery,
  useGetRelocationOrderByIdQuery,
  useAcceptRelocationOrderMutation,
  useRejectRelocationOrderMutation,
  useCounterRelocationOrderMutation,
  RelocationOrder,
} from "../../redux/services/relocationApi";
import { usePermission } from "../../utils/permissions";

type FilterTab = "all" | "pending" | "counter_sent" | "accepted" | "rejected";

const FILTER_TABS: { label: string; value: FilterTab }[] = [
  { label: "All", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Countered", value: "counter_sent" },
  { label: "Accepted", value: "accepted" },
  { label: "Rejected", value: "rejected" },
];

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  pending:            { label: "Pending",             color: "text-yellow-700", bg: "bg-yellow-50 border-yellow-200" },
  counter_sent:       { label: "Counter Sent",        color: "text-blue-700",   bg: "bg-blue-50 border-blue-200" },
  customer_countered: { label: "Customer Countered",  color: "text-orange-700", bg: "bg-orange-50 border-orange-200" },
  customer_accepted:  { label: "Customer Accepted",   color: "text-green-700",  bg: "bg-green-50 border-green-200" },
  accepted:           { label: "Accepted",            color: "text-green-700",  bg: "bg-green-50 border-green-200" },
  rejected:           { label: "Rejected",            color: "text-red-700",    bg: "bg-red-50 border-red-200" },
  delivered:          { label: "Delivered",           color: "text-gray-700",   bg: "bg-gray-50 border-gray-200" },
  picked_up:          { label: "Picked Up",           color: "text-indigo-700", bg: "bg-indigo-50 border-indigo-200" },
};

const STATUS_EXPLANATIONS: Record<string, string> = {
  counter_sent: "You've sent a new price to the customer. Waiting for them to accept, reject, or counter back.",
  customer_countered: "The customer rejected your counter price and proposed their own — action needed.",
  customer_accepted: "The customer agreed to your counter price, but the order isn't finalized until you Accept it here.",
};

const formatPrice = (n: number) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", minimumFractionDigits: 0 }).format(n);

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });

const formatReceived = (d: string) =>
  new Date(d).toLocaleString("en-NG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const getInitials = (name?: string | null) =>
  name ? name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) : "?";

const MOVE_SIZE_LABELS: Record<string, string> = {
  "1_bedroom":    "1 Bedroom",
  "2_bedroom":    "2 Bedrooms",
  "3_bedroom":    "3 Bedrooms",
  "warehouse":    "Warehouse",
  "office_space": "Office Space",
};
const formatMoveSize = (s: string) => MOVE_SIZE_LABELS[s] ?? s.replace(/_/g, " ");

// ── Negotiation Detail Modal ──────────────────────────────────────────────────

const NegotiationDetailModal = ({
  orderId,
  onClose,
  onCounter,
}: {
  orderId: string;
  onClose: () => void;
  onCounter: (order: RelocationOrder) => void;
}) => {
  const { data, isLoading } = useGetRelocationOrderByIdQuery(orderId);
  const order = data?.data?.data;

  const [acceptOrder, { isLoading: accepting }] = useAcceptRelocationOrderMutation();
  const [rejectOrder, { isLoading: rejecting }] = useRejectRelocationOrderMutation();

  const { can } = usePermission();
  const canAccept = can("accept relocation order");
  const canReject = can("reject relocation order");
  const canCounter = can("send relocation counter offer");

  const handleAccept = async () => {
    if (!order) return;
    try {
      await acceptOrder({ id: order.id }).unwrap();
      toast.success("Order accepted successfully.");
      onClose();
    } catch {
      toast.error("Failed to accept order.");
    }
  };

  const handleReject = async () => {
    if (!order) return;
    try {
      await rejectOrder({ id: order.id }).unwrap();
      toast.success("Order rejected.");
      onClose();
    } catch {
      toast.error("Failed to reject order.");
    }
  };

  const canAct = order?.status === "pending" || order?.status === "customer_countered" || order?.status === "customer_accepted";

  return (
    <FleetrifyModal isOpen onClose={onClose} title="Order & Negotiation Details" className="max-w-lg">
      <div className="overflow-y-auto max-h-[75vh] p-4 space-y-5">
        {isLoading || !order ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={28} className="animate-spin text-[#143887]" />
          </div>
        ) : (
          <>
            {/* Header: status + type */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full border ${
                STATUS_CONFIG[order.status]?.bg ?? "bg-gray-100 border-gray-200"
              } ${STATUS_CONFIG[order.status]?.color ?? "text-gray-600"}`}>
                {STATUS_CONFIG[order.status]?.label ?? order.status}
                {STATUS_EXPLANATIONS[order.status] && (
                  <InfoTooltip position="bottom" text={STATUS_EXPLANATIONS[order.status]} />
                )}
              </span>
              <span className="text-xs font-semibold px-3 py-1 rounded-full bg-yellow-50 border border-yellow-200 text-yellow-700">
                <span className="inline-flex items-center gap-1">
                  <Truck size={10} />
                  {order.order_type === "interstate_relocation" ? "Interstate" : "Local"} Relocation
                </span>
              </span>
              {order.order_number && (
                <span className="text-xs text-gray-400 ml-auto">{order.order_number}</span>
              )}
            </div>

            {/* Customer */}
            <div className="bg-[#F5F6FA] rounded-lg p-4 space-y-1.5">
              <p className="text-[10px] text-[#828282] uppercase tracking-widest font-semibold mb-2">Customer</p>
              <div className="flex items-center gap-2 text-xs">
                <User size={12} className="text-[#828282] shrink-0" />
                <span className="font-medium text-[#00032A]">{order.customer?.name ?? "Unknown customer"}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-[#828282]">
                <Phone size={12} className="shrink-0" />
                <span>{order.customer?.phone ?? "—"}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-[#828282]">
                <Mail size={12} className="shrink-0" />
                <span>{order.customer?.email ?? "—"}</span>
              </div>
            </div>

            {/* Route */}
            <div className="bg-[#F5F6FA] rounded-lg p-4">
              <p className="text-[10px] text-[#828282] uppercase tracking-widest font-semibold mb-3">Route</p>
              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center mt-1 shrink-0">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#143887]" />
                  <div className="w-px h-6 bg-gray-300 my-0.5" />
                  <MapPin size={13} className="text-red-500" />
                </div>
                <div className="flex-1 space-y-3">
                  <div>
                    <p className="text-[10px] text-[#828282] uppercase">Pickup</p>
                    <p className="text-xs font-medium text-[#00032A]">{order.pickup_location || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#828282] uppercase">Destination</p>
                    <p className="text-xs font-medium text-[#00032A]">{order.destination || "—"}</p>
                  </div>
                </div>
              </div>
              {(order.pickup_date || order.pickup_time) && (
                <div className="flex gap-4 mt-3 pt-3 border-t border-gray-200">
                  {order.pickup_date && (
                    <div className="flex items-center gap-1.5 text-xs text-[#828282]">
                      <Calendar size={11} />
                      <span className="font-medium text-[#00032A]">
                        {new Date(order.pickup_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                      </span>
                    </div>
                  )}
                  {order.pickup_time && (
                    <div className="flex items-center gap-1.5 text-xs text-[#828282]">
                      <Clock size={11} />
                      <span className="font-medium text-[#00032A]">{order.pickup_time.slice(0, 5)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Price Negotiation Timeline */}
            <div className="bg-[#F5F6FA] rounded-lg p-4">
              <p className="text-[10px] text-[#828282] uppercase tracking-widest font-semibold mb-4">Price Negotiation</p>
              <div className="space-y-0">
                {(() => {
                  type Step = { side: "company" | "customer"; label: string; note?: string; price: string; isLatest?: boolean; isAccepted?: boolean };
                  const steps: Step[] = [];
                  steps.push({ side: "customer", label: "Customer's Requested Price", price: order.price });

                  if (order.counter_offer) {
                    const co = order.counter_offer;
                    if (co.proposed_by === "company") {
                      if (co.customer_offered_price) steps.push({ side: "customer", label: "Customer's Counter Offer", price: co.customer_offered_price });
                      if (co.company_offered_price)  steps.push({ side: "company",   label: "Your Counter Offer", note: co.description || undefined, price: co.company_offered_price, isLatest: order.status !== "customer_accepted" });
                    } else {
                      if (co.company_offered_price)  steps.push({ side: "company",   label: "Your Counter Offer", price: co.company_offered_price });
                      if (co.customer_offered_price) steps.push({ side: "customer", label: "Customer's Counter Offer", note: co.description || undefined, price: co.customer_offered_price, isLatest: order.status !== "customer_accepted" });
                    }
                    if (order.status === "customer_accepted") {
                      steps.push({ side: "customer", label: "Customer Accepted", price: co.company_offered_price ?? order.price, isLatest: true, isAccepted: true });
                    }
                  }

                  return steps.map((step, i) => {
                    const isLast = i === steps.length - 1;
                    const isCompany = step.side === "company";
                    const dotBg = step.isAccepted ? "bg-green-500 ring-green-400" : isCompany ? "bg-[#143887] ring-[#143887]" : "bg-gray-400 ring-gray-300";
                    const amtColor = step.isLatest ? (step.isAccepted ? "text-green-600" : isCompany ? "text-[#143887]" : "text-orange-600") : "text-[#828282]";
                    const labelColor = step.isLatest ? (step.isAccepted ? "text-green-700" : isCompany ? "text-[#143887]" : "text-orange-700") : "text-[#00032A]";
                    const badgeBg = isCompany ? "bg-blue-100 text-[#143887]" : step.isAccepted ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600";
                    return (
                      <div key={i} className="flex items-start gap-3">
                        <div className="flex flex-col items-center shrink-0">
                          <div className={`w-3 h-3 rounded-full border-2 border-white ring-2 mt-1 ${dotBg}`} />
                          {!isLast && <div className="w-px flex-1 min-h-[28px] bg-gray-300 my-1" />}
                        </div>
                        <div className={`flex-1 ${isLast ? "" : "pb-3"}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${badgeBg}`}>{isCompany ? "You" : "Customer"}</span>
                                <p className={`text-xs font-semibold ${labelColor}`}>{step.label}</p>
                              </div>
                              {step.note && <p className="text-[11px] text-[#828282] italic">"{step.note}"</p>}
                            </div>
                            <span className={`text-sm font-bold whitespace-nowrap ${amtColor}`}>{formatPrice(parseFloat(step.price))}</span>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Current best offer summary */}
              <div className="mt-4 pt-3 border-t border-gray-200 flex items-center justify-between">
                <span className="text-xs text-[#828282]">
                  {order.status === "customer_accepted"
                    ? "Customer accepted at"
                    : order.counter_offer?.proposed_by === "customer"
                    ? "Customer's latest offer"
                    : order.counter_offer?.company_offered_price
                    ? "Your current offer"
                    : "Customer's quoted price"}
                </span>
                <span className="text-lg font-bold text-[#143887]">
                  {formatPrice(parseFloat(
                    order.counter_offer
                      ? order.counter_offer.proposed_by === "company"
                        ? order.counter_offer.company_offered_price ?? order.price
                        : order.counter_offer.customer_offered_price ?? order.price
                      : order.price
                  ))}
                </span>
              </div>
              {order.route?.price && (
                <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-between">
                  <span className="text-xs text-[#828282]">Route base rate</span>
                  <span className="text-sm font-semibold text-gray-500">{formatPrice(order.route.price)}</span>
                </div>
              )}
            </div>

            {/* Action buttons */}
            {canAct && (
              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleAccept}
                  disabled={accepting || rejecting || !canAccept}
                  title={canAccept ? "Confirms the current price and converts this request into a live order." : "You don't have permission to accept relocation orders."}
                  className="flex-1 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  {accepting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                  Accept
                </button>
                {order.status !== "customer_accepted" && (
                  <button
                    onClick={() => { onClose(); onCounter(order); }}
                    disabled={accepting || rejecting || !canCounter}
                    title={canCounter ? "Propose a different price to the customer — the request stays open until they respond." : "You don't have permission to send a counter offer."}
                    className="flex-1 py-2.5 rounded-lg bg-[#143887] hover:bg-[#0f2d6b] text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                  >
                    <MessageSquare size={14} />
                    Counter
                  </button>
                )}
                <button
                  onClick={handleReject}
                  disabled={accepting || rejecting || !canReject}
                  title={canReject ? "Permanently declines the request — the customer is notified and no order is created." : "You don't have permission to reject relocation orders."}
                  className="flex-1 py-2.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  {rejecting ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                  Reject
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </FleetrifyModal>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const OrderRequests: React.FC = () => {
  const [activeTab, setActiveTab] = useState<FilterTab>("all");
  const [counterTarget, setCounterTarget] = useState<RelocationOrder | null>(null);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);

  const { data, isLoading, isError } = useGetRelocationOrdersQuery(
    { per_page: 100, sort: "desc" },
    { pollingInterval: 30000 }
  );

  const allRequests = useMemo<RelocationOrder[]>(() => {
    if (!data?.data?.data) return [];
    return data.data.data;
  }, [data]);

  const counts = data?.data?.counts;

  const stats = useMemo(() => ({
    total:     counts?.total     ?? allRequests.length,
    pending:   counts?.pending   ?? allRequests.filter((r) => r.status === "pending").length,
    countered: counts?.counter_sent ?? allRequests.filter((r) => r.status === "counter_sent").length,
    accepted:  counts?.accepted  ?? allRequests.filter((r) => r.status === "accepted").length,
  }), [counts, allRequests]);

  const requests = useMemo(() => {
    if (activeTab === "all") return allRequests;
    if (activeTab === "counter_sent")
      return allRequests.filter((r) => r.status === "counter_sent" || r.status === "customer_countered");
    return allRequests.filter((r) => r.status === activeTab);
  }, [allRequests, activeTab]);

  if (isError) return (
    <DashboardLayout title="Order Requests" description="Incoming relocation service requests awaiting price negotiation">
      <div className="flex flex-col items-center justify-center py-24 gap-3 text-center">
        <p className="text-sm font-medium text-red-600">Failed to load order requests.</p>
        <button onClick={() => window.location.reload()} className="px-4 py-2 text-sm rounded-md border border-red-300 text-red-600 hover:bg-red-50">
          Retry
        </button>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout
      title="Order Requests"
      description="Incoming relocation service requests awaiting price negotiation"
    >
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
        {[
          { label: "Total Requests", value: stats.total,     color: "text-[#143887]" },
          { label: "Pending",        value: stats.pending,   color: "text-yellow-600" },
          { label: "Counter Sent",   value: stats.countered, color: "text-blue-600" },
          { label: "Accepted",       value: stats.accepted,  color: "text-green-600" },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07, duration: 0.35 }}
            className="bg-white rounded-xl border border-gray-100 shadow-sm p-4"
          >
            <p className="text-xs text-gray-500 font-medium">{stat.label}</p>
            {isLoading ? (
              <div className="h-8 w-10 bg-gray-200 animate-pulse rounded mt-1" />
            ) : (
              <p className={`text-2xl font-bold mt-1 ${stat.color}`}>{stat.value}</p>
            )}
          </motion.div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mt-6 bg-gray-100 p-1 rounded-xl overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === tab.value
                ? "bg-white text-[#143887] shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
            {tab.value === "pending" && stats.pending > 0 && (
              <span className="ml-1.5 bg-yellow-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {stats.pending}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Request cards */}
      <div className="mt-6 space-y-4 pb-10">
        <AnimatePresence mode="popLayout">
          {requests.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-20 text-gray-400"
            >
              <Package size={48} className="opacity-40 mb-3" />
              <p className="font-medium text-gray-500">No requests found</p>
              <p className="text-sm mt-1">New relocation requests will appear here</p>
            </motion.div>
          ) : (
            requests.map((req, index) => {
              const status = STATUS_CONFIG[req.status] ?? STATUS_CONFIG.pending;

              return (
                <motion.div
                  key={req.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -40, scale: 0.96 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setDetailOrderId(req.id)}
                >
                  <div className="p-4 sm:p-5">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4 lg:gap-5">

                      {/* Customer info */}
                      <div className="flex items-start gap-3 sm:w-44 lg:w-52 flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-[#143887] flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                          {getInitials(req.customer?.name)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-[#00032A] text-sm">{req.customer?.name ?? "Unknown customer"}</p>
                          <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                            <Phone size={11} className="flex-shrink-0" />
                            <span>{req.customer?.phone ?? "—"}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                            <Mail size={11} className="flex-shrink-0" />
                            <span className="truncate">{req.customer?.email ?? "—"}</span>
                          </div>
                        </div>
                      </div>

                      {/* Route + details */}
                      <div className="flex-1 min-w-0">
                        {/* Order type + vehicle */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center gap-1 text-xs font-semibold bg-yellow-50 text-yellow-700 border border-yellow-200 px-2 py-0.5 rounded-full">
                            <Truck size={10} />
                            {req.order_type === "interstate_relocation" ? "INTERSTATE" : "LOCAL"} RELOCATION
                          </span>
                          {req.vehicle_type && (
                            <span className="text-xs text-gray-500">
                              Vehicle: <span className="font-medium text-gray-700 capitalize">{req.vehicle_type}</span>
                            </span>
                          )}
                        </div>

                        {/* Route */}
                        <div className="mt-2.5 space-y-1">
                          <div className="flex items-center gap-1.5 text-xs">
                            <MapPin size={12} className="text-[#143887] flex-shrink-0" />
                            <span className="text-gray-400 w-14 shrink-0">Pickup:</span>
                            <span className="text-gray-800 font-medium">{req.pickup_location || "—"}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs">
                            <MapPin size={12} className="text-red-500 flex-shrink-0" />
                            <span className="text-gray-400 w-14 shrink-0">Dropoff:</span>
                            <span className="text-gray-800 font-medium">{req.destination || "—"}</span>
                          </div>
                        </div>

                        {/* Schedule */}
                        <div className="flex items-center gap-4 mt-2 flex-wrap">
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Calendar size={11} />
                            <span className="text-gray-400">Move Date:</span>
                            <span className="font-medium text-gray-700">{req.pickup_date ? formatDate(req.pickup_date) : "Today"}</span>
                          </div>
                          {req.pickup_time && (
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <Clock size={11} />
                              <span className="text-gray-400">Time:</span>
                              <span className="font-medium text-gray-700">{req.pickup_time.slice(0, 5)}</span>
                            </div>
                          )}
                        </div>

                        {/* Relocation details */}
                        {(req.move_type || req.move_size || req.inventory_description || req.contact_name || (req.extra_stops && req.extra_stops.length > 0)) && (
                          <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                              Relocation Details
                            </p>

                            {/* Property type & size */}
                            {(req.move_type || req.move_size) && (
                              <div className="flex items-center gap-1.5 text-xs">
                                {req.move_type === "residential" ? <Home size={11} className="text-purple-500 flex-shrink-0" /> : <Building2 size={11} className="text-purple-500 flex-shrink-0" />}
                                <span className="text-gray-400">Property:</span>
                                <span className="font-medium text-gray-700">
                                  {req.move_type ? (req.move_type === "residential" ? "Residential" : "Commercial") : ""}
                                  {req.move_type && req.move_size ? " · " : ""}
                                  {req.move_size ? formatMoveSize(req.move_size) : ""}
                                </span>
                              </div>
                            )}

                            {/* Add-ons */}
                            {(req.has_loading_unloading || req.has_packaging_materials) && (
                              <div className="flex items-center gap-1.5 text-xs">
                                <Layers size={11} className="text-green-500 flex-shrink-0" />
                                <span className="text-gray-400">Add-ons:</span>
                                <span className="font-medium text-gray-700">
                                  {[
                                    req.has_loading_unloading && "Loading & Unloading",
                                    req.has_packaging_materials && "Packaging Materials",
                                  ].filter(Boolean).join(", ")}
                                </span>
                              </div>
                            )}

                            {/* Inventory */}
                            {req.inventory_description && (
                              <div className="flex items-start gap-1.5 text-xs">
                                <FileText size={11} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-400 shrink-0">Items/Inventory:</span>
                                <span className="text-gray-700 line-clamp-2">{req.inventory_description}</span>
                              </div>
                            )}

                            {/* On-site contact */}
                            {req.contact_name && (
                              <div className="flex items-center gap-1.5 text-xs">
                                <User size={11} className="text-gray-400 flex-shrink-0" />
                                <span className="text-gray-400">On-site Contact:</span>
                                <span className="font-medium text-gray-700">{req.contact_name}</span>
                                {req.contact_phone && (
                                  <span className="text-gray-500">({req.contact_phone})</span>
                                )}
                              </div>
                            )}

                            {/* Extra stops */}
                            {req.extra_stops && req.extra_stops.length > 0 && (
                              <div className="flex items-start gap-1.5 text-xs">
                                <ArrowRight size={11} className="text-orange-400 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-400 shrink-0">Extra Stops:</span>
                                <span className="text-gray-700">{req.extra_stops.join(" · ")}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Price + status */}
                      <div className="flex flex-col gap-2 sm:items-end sm:min-w-[140px] shrink-0">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${status.bg} ${status.color}`}>
                          {status.label}
                        </span>
                        <div className="sm:text-right">
                          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Quoted Price</p>
                          <p className="text-lg font-bold text-[#143887]">{formatPrice(parseFloat(req.price))}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer timestamp */}
                  <div className="px-4 sm:px-5 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                    <p className="text-xs text-gray-400">Received {formatReceived(req.created_at)}</p>
                    <button
                      onClick={() => setDetailOrderId(req.id)}
                      className="flex items-center gap-1 text-xs text-[#143887] font-medium hover:underline"
                    >
                      View details <ChevronRight size={12} />
                    </button>
                  </div>
                </motion.div>
              );
            })
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {detailOrderId && (
          <NegotiationDetailModal
            orderId={detailOrderId}
            onClose={() => setDetailOrderId(null)}
            onCounter={(order) => setCounterTarget(order)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {counterTarget && (
          <CounterOfferModal
            request={counterTarget}
            onClose={() => setCounterTarget(null)}
          />
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
};

export default OrderRequests;
